import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useDistance } from '../hooks/useDistance';
import { useFirmendaten } from '../hooks/useFirmendaten';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Calendar, MessageSquare, ArrowLeft, Edit, Trash2, Image as ImageIcon, Plus, Pencil, FileText, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Download, File, FileSpreadsheet, ClipboardList, FileSignature, Share2, Eye, Search, X, Ruler, FolderKanban, ExternalLink, Building2, Sparkles, Loader2, PenTool } from '../icons/wp-icons';
import { getKundeById, updateKunde, deleteKunde, downloadAllFiles, createKunde, createKundeTelefon, updateKundeTelefon, deleteKundeTelefon } from '../api/kundenApi';
import { getFotosByKundeId, uploadFoto } from '../api/fotoApi';
import { dokumentApi } from '../api/dokumentApi';
import { updateAnfrageStatus, updateAnfrage, createAnfrage, deleteAnfrage, zusammenfassungAnfrage } from '../api/anfrageApi';
import { getProjekteByKunde } from '../api/projektApi';
import { rechnungApi } from '../api/rechnungApi';
import Layout from '../components/Layout/Layout';
import Modal from '../components/Common/Modal';
import KundenForm from '../components/Kunde/KundenForm';
import AnfrageForm from '../components/Anfrage/AnfrageForm';
import AufmassModal from '../components/Anfrage/AufmassModal';
import FotoUpload from '../components/Foto/FotoUpload';
import DokumenteTab from '../components/Kunde/DokumenteTab';
import SignaturePad from '../components/Unterschrift/SignaturePad';
import UnterschriftenListe from '../components/Unterschrift/UnterschriftenListe';
import ArbeitsnachweisModal from '../components/Arbeitsnachweis/ArbeitsnachweisModal';
import SkizzenPad from '../components/Skizze/SkizzenPad';
import AktivitaetenTimeline from '../components/Kunde/AktivitaetenTimeline';
import AnlagenSection from '../components/Kunde/AnlagenSection';
import ObjekteSection from '../components/Kunde/ObjekteSection';
import { unterschriftApi } from '../api/unterschriftApi';
import { getBestellungenByLieferant } from '../api/bestellungApi';
import { useCustomerTransition } from '../context/CustomerTransitionContext';
import { useKundenRefresh, useAnfragenRefresh, useTermineRefresh, useFotosRefresh } from '../hooks/useDataRefresh';
import { usePermissions, MODULES } from '../context/PermissionContext';
import { useAuth } from '../context/AuthContext';

// Animation variants for staggered content
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    }
  }
};

const slideFromLeftVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    }
  }
};

const slideFromRightVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    }
  }
};

const fadeInVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
    }
  }
};

function Kundendetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightAnfrageId = searchParams.get('anfrageId');
  const { transitionData, clearTransition } = useCustomerTransition();
  const { canEdit } = usePermissions();
  const canEditKunden = canEdit(MODULES.KUNDEN);
  const { firmendaten } = useFirmendaten();
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';

  // Use transition data as initial state if available (from prefetch)
  const initialKunde = transitionData?.kunde?.id === parseInt(id) ? transitionData.kunde : null;

  const [kunde, setKunde] = useState(initialKunde);
  const [isPageReady, setIsPageReady] = useState(false);
  const [isContentVisible, setIsContentVisible] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [dokumente, setDokumente] = useState([]);
  const [loading, setLoading] = useState(!initialKunde);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isAnfrageModalOpen, setIsAnfrageModalOpen] = useState(false);
  const [editingAnfrage, setEditingAnfrage] = useState(null);
  const [isAnfrageSaving, setIsAnfrageSaving] = useState(false);
  const [anfrageError, setAnfrageError] = useState('');
  const [activeMediaTab, setActiveMediaTab] = useState('fotos');
  const [activeAnfragenProjekteTab, setActiveAnfragenProjekteTab] = useState('anfragen');
  const [projekte, setProjekte] = useState([]);
  const [lieferantBestellungen, setLieferantBestellungen] = useState([]);
  const [expandedProjekte, setExpandedProjekte] = useState({});
  const [projektSearchTerm, setProjektSearchTerm] = useState('');
  const [projektStatusFilter, setProjektStatusFilter] = useState(null);
  const [showProjektSignaturePad, setShowProjektSignaturePad] = useState(null); // projektId oder null
  const [refreshProjektUnterschriften, setRefreshProjektUnterschriften] = useState({});
  const [isDeleteAnfrageModalOpen, setIsDeleteAnfrageModalOpen] = useState(false);
  const [isDeletingAnfrage, setIsDeletingAnfrage] = useState(false);
  const [expandedAnfragen, setExpandedAnfragen] = useState({});
  const [anfrageSearchTerm, setAnfrageSearchTerm] = useState('');
  const [anfrageStatusFilter, setAnfrageStatusFilter] = useState(null); // null = alle, 'offen', 'erledigt'
  const [lieferantApFilter, setLieferantApFilter] = useState(null); // Ansprechpartner-Filter für Lieferanten-Ansicht
  const [isApModalOpen, setIsApModalOpen] = useState(false);
  const [anfragenPage, setAnfragenPage] = useState(0);
  const [terminePage, setTerminePage] = useState(0);
  const ITEMS_PER_PAGE = 5;

  // Sekundärnummern (Multi-Tel) — Inline-Edit-State.
  const [neueZusatzNummer, setNeueZusatzNummer] = useState('');
  const [neueZusatzLabel, setNeueZusatzLabel] = useState('');
  const [zusatzAddOpen, setZusatzAddOpen] = useState(false);
  const [zusatzSaving, setZusatzSaving] = useState(false);

  const handleAddZusatzTelefon = async () => {
    if (!neueZusatzNummer.trim() || !kunde?.id) return;
    setZusatzSaving(true);
    try {
      const created = await createKundeTelefon(kunde.id, {
        nummer: neueZusatzNummer.trim(),
        label: neueZusatzLabel.trim() || null
      });
      setKunde(prev => ({
        ...prev,
        zusatzTelefon: [...(prev?.zusatzTelefon || []), created]
      }));
      setNeueZusatzNummer('');
      setNeueZusatzLabel('');
      setZusatzAddOpen(false);
    } catch (e) {
      console.error('Sekundärnummer anlegen fehlgeschlagen:', e);
      setError('Sekundärnummer konnte nicht gespeichert werden.');
    } finally {
      setZusatzSaving(false);
    }
  };

  const handleDeleteZusatzTelefon = async (telefonId) => {
    if (!kunde?.id) return;
    try {
      await deleteKundeTelefon(kunde.id, telefonId);
      setKunde(prev => ({
        ...prev,
        zusatzTelefon: (prev?.zusatzTelefon || []).filter(t => t.id !== telefonId)
      }));
    } catch (e) {
      console.error('Sekundärnummer löschen fehlgeschlagen:', e);
      setError('Sekundärnummer konnte nicht gelöscht werden.');
    }
  };
  const [isAufmassModalOpen, setIsAufmassModalOpen] = useState(false);
  const [selectedAnfrageForAufmass, setSelectedAnfrageForAufmass] = useState(null);
  const [editingAufmass, setEditingAufmass] = useState(null);
  const [isAufmassSaving, setIsAufmassSaving] = useState(false);
  const [summarizingAnfrageId, setSummarizingAnfrageId] = useState(null);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [mapPosition, setMapPosition] = useState({ top: 0, left: 0 });
  const mapPreviewTimeout = useRef(null);
  const addressRef = useRef(null);
  const anfrageRefs = useRef({});

  const handleAddressMouseEnter = (e) => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
    // Position basierend auf Mauszeiger
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    mapPreviewTimeout.current = setTimeout(() => {
      // Popup rechts unten vom Zeiger, aber innerhalb des Viewports halten
      const popupWidth = 320;
      const popupHeight = 280;

      let left = mouseX + 15;
      let top = mouseY + 15;

      // Prüfen ob Popup rechts aus dem Viewport ragt
      if (left + popupWidth > window.innerWidth - 10) {
        left = mouseX - popupWidth - 15;
      }

      // Prüfen ob Popup unten aus dem Viewport ragt
      if (top + popupHeight > window.innerHeight - 10) {
        top = mouseY - popupHeight - 15;
      }

      setMapPosition({ top, left });
      setShowMapPreview(true);
    }, 400);
  };

  const handleAddressMouseLeave = () => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
    mapPreviewTimeout.current = setTimeout(() => {
      setShowMapPreview(false);
    }, 300);
  };

  const handleMapMouseEnter = () => {
    if (mapPreviewTimeout.current) {
      clearTimeout(mapPreviewTimeout.current);
    }
  };

  const handleMapMouseLeave = () => {
    setShowMapPreview(false);
  };
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(null); // anfrageId oder null
  const [showSkizzenPad, setShowSkizzenPad] = useState(null); // { anfrageId, imageUrl? } oder null
  const [isSkizzeSaving, setIsSkizzeSaving] = useState(false);
  const [showArbeitsnachweisModal, setShowArbeitsnachweisModal] = useState(null); // anfrageId oder null
  const [refreshUnterschriften, setRefreshUnterschriften] = useState({});
  const [refreshTimeline, setRefreshTimeline] = useState(0);

  // iOS Share Dialog State
  const [showIOSShareDialog, setShowIOSShareDialog] = useState(false);
  const [iosShareData, setIosShareData] = useState(null);

  // Entfernung und Fahrzeit zum Kunden berechnen
  const { formattedDistance, formattedDriveTime, loading: distanceLoading } = useDistance(kunde?.adresse, firmendaten);

  // Prüfen ob ein Modal offen ist
  const isAnyModalOpen = isModalOpen || isDeleteModalOpen || isAnfrageModalOpen || isDeleteAnfrageModalOpen || showSignaturePad || showProjektSignaturePad || showSkizzenPad || isAufmassModalOpen || isApModalOpen;

  // Keyboard Shortcuts für Kundendetail
  const shortcuts = useMemo(() => ({
    'n': () => {
      if (!isAnyModalOpen) {
        openNewAnfrageModal();
      }
    },
    't': () => {
      if (!isAnyModalOpen && kunde) {
        navigate(`/kalender?neuerTermin=true&kundeId=${id}&kundeName=${encodeURIComponent(kunde.name)}&kundeEmail=${encodeURIComponent(kunde.email || '')}&kundeAdresse=${encodeURIComponent(kunde.adresse || '')}`);
      }
    },
    'e': () => {
      if (!isAnyModalOpen) {
        setIsModalOpen(true);
      }
    },
  }), [isAnyModalOpen, kunde, id, navigate]);

  useKeyboardShortcuts(shortcuts, !isAnyModalOpen);

  const toggleAnfrage = (anfrageId) => {
    setExpandedAnfragen(prev => ({
      ...prev,
      [anfrageId]: !prev[anfrageId]
    }));
  };

  const getFotoUrl = (foto) => {
    const baseURL = import.meta.env.VITE_API_URL?.replace('/api', '') || '';
    // Wenn dateipfad bereits /api/uploads enthält, direkt verwenden
    if (foto.dateipfad?.startsWith('/api/uploads')) {
      return `${baseURL}${foto.dateipfad}`;
    }
    // Fallback für alte Fotos ohne vollständigen Pfad
    const filename = foto.dateipfad?.split('/').pop() || foto.dateiname;
    return `${baseURL}/api/uploads/${filename}`;
  };

  const getDocIcon = (dateityp) => {
    if (dateityp?.includes('pdf')) {
      return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
    }
    if (dateityp?.includes('word') || dateityp?.includes('document')) {
      return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
    }
    if (dateityp?.includes('excel') || dateityp?.includes('spreadsheet')) {
      return { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50' };
    }
    return { icon: File, color: 'text-gray-500', bg: 'bg-gray-50' };
  };

  const handleDokumentDownload = async (dokument) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiUrl}/dokumente/download/${dokument.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();

      // Mobile Detection
      const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      // iOS: Zeige Dialog mit Share-Option
      if (isIOS) {
        const url = window.URL.createObjectURL(blob);
        setIosShareData({ url, blob, filename: dokument.originalname, mimeType: blob.type });
        setShowIOSShareDialog(true);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = dokument.originalname;
      a.style.display = 'none';
      document.body.appendChild(a);

      // Für Mobile (Android): Timeout vor Klick für bessere Kompatibilität
      if (isMobile) {
        setTimeout(() => {
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 250);
        }, 100);
      } else {
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
    } catch (error) {
      console.error('Download Fehler:', error);
    }
  };

  // iOS Share-Funktion
  const handleIOSShare = async () => {
    if (!iosShareData) return;

    try {
      // Versuche File-Konstruktor (funktioniert auf neueren iOS-Versionen)
      if (typeof File !== 'undefined' && navigator.share && navigator.canShare) {
        try {
          const file = new File([iosShareData.blob], iosShareData.filename, { type: iosShareData.mimeType });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: iosShareData.filename
            });
            closeIOSShareDialog();
            return;
          }
        } catch (fileError) {
          console.log('File-Konstruktor nicht verfügbar:', fileError);
        }
      }
      // Fallback: Öffne in neuem Tab
      window.open(iosShareData.url, '_blank');
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback: Öffne in neuem Tab
        window.open(iosShareData.url, '_blank');
      }
    }
    closeIOSShareDialog();
  };

  const closeIOSShareDialog = () => {
    if (iosShareData?.url) {
      window.URL.revokeObjectURL(iosShareData.url);
    }
    setShowIOSShareDialog(false);
    setIosShareData(null);
  };

  const handlePdfOpen = async (dokument) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiUrl}/dokumente/download/${dokument.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('PDF konnte nicht geladen werden');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF Fehler:', error);
    }
  };

  const isPdf = (dateityp) => dateityp?.includes('pdf');

  useEffect(() => {
    loadKunde();
    loadFotos();
    loadDokumente();
    loadProjekte();
  }, [id]);

  // Echtzeit-Updates: Reagiere auf KI-Assistent Aktionen
  const handleDataRefresh = useCallback((detail) => {
    // Nur aktualisieren wenn es diesen Kunden betrifft oder kein spezifischer Kunde angegeben
    if (!detail.kundeId || detail.kundeId === id || detail.kundeId === parseInt(id)) {
      loadKunde();
    }
  }, [id]);

  // Foto-Updates: Reagiere auf foto_speichern vom KI-Assistenten
  const handleFotoRefresh = useCallback((detail) => {
    if (!detail.kundeId || detail.kundeId === id || detail.kundeId === parseInt(id)) {
      loadFotos();
      setRefreshTimeline(prev => prev + 1);
    }
  }, [id]);

  // Abonniere Kunden-, Anfragen-, Termin- und Foto-Updates
  useKundenRefresh(handleDataRefresh, id);
  useAnfragenRefresh(handleDataRefresh, id);
  useTermineRefresh(handleDataRefresh, id);
  useFotosRefresh(handleFotoRefresh, id);

  // Smooth animation sequence after page loads
  useEffect(() => {
    if (kunde) {
      // Step 1: Clear transition overlay immediately
      clearTransition();

      // Step 2: Show page structure immediately
      requestAnimationFrame(() => {
        setIsPageReady(true);
        // Step 3: Show content right after
        requestAnimationFrame(() => {
          setIsContentVisible(true);
        });
      });
    }
  }, [kunde, clearTransition]);

  // Auto-expand Anfrage wenn anfrageId als Query-Parameter vorhanden ist
  useEffect(() => {
    if (!highlightAnfrageId || !kunde?.anfragen) return;

    // Sicherstellen, dass der Anfragen-Tab aktiv ist
    setActiveAnfragenProjekteTab('anfragen');

    // Status-Filter zurücksetzen damit die Anfrage sichtbar ist
    setAnfrageStatusFilter(null);
    setAnfrageSearchTerm('');

    // Richtige Seite berechnen und setzen (IDs sind UUIDs/Strings)
    const anfrageIndex = kunde.anfragen.findIndex(a => String(a.id) === String(highlightAnfrageId));
    if (anfrageIndex >= 0) {
      const targetAnfrageId = kunde.anfragen[anfrageIndex].id;
      const page = Math.floor(anfrageIndex / ITEMS_PER_PAGE);
      setAnfragenPage(page);

      // Anfrage aufklappen
      setExpandedAnfragen(prev => ({ ...prev, [targetAnfrageId]: true }));

      // Zum Element scrollen nach kurzem Delay (DOM muss gerendert sein)
      setTimeout(() => {
        const el = anfrageRefs.current[targetAnfrageId];
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }

    // Query-Parameter entfernen damit bei Reload nicht nochmal getriggert wird
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('anfrageId');
    setSearchParams(newParams, { replace: true });
  }, [highlightAnfrageId, kunde?.anfragen]);

  const loadKunde = async () => {
    try {
      const data = await getKundeById(id);
      setKunde(data);
    } catch (error) {
      console.error('Error loading kunde:', error);
      // Bei 404 zur Kundenliste navigieren
      if (error.response?.status === 404) {
        navigate('/kunden');
        return;
      }
      // Bei anderen Fehlern: Kunde auf null setzen (außer wir haben bereits prefetched data)
      if (!initialKunde) {
        setKunde(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadFotos = async () => {
    try {
      const data = await getFotosByKundeId(id);
      setFotos(data);
    } catch (error) {
      console.error('Error loading fotos:', error);
    }
  };

  const loadDokumente = async () => {
    try {
      const isHauptLieferant = kunde?.status === 'lieferant' && !kunde?.hauptLieferantId;
      const data = await dokumentApi.getDokumenteByKunde(id, { inkludiereAnsprechpartner: isHauptLieferant });
      setDokumente(data);
    } catch (error) {
      console.error('Error loading dokumente:', error);
    }
  };

  const loadProjekte = async () => {
    try {
      const data = await getProjekteByKunde(id);
      setProjekte(data);
    } catch (error) {
      console.error('Error loading projekte:', error);
    }
  };

  const loadLieferantBestellungen = useCallback(async () => {
    if (kunde?.status !== 'lieferant') return;
    try {
      const data = await getBestellungenByLieferant(id);
      setLieferantBestellungen(data);
    } catch (error) {
      console.error('Error loading lieferant bestellungen:', error);
    }
  }, [id, kunde?.status]);

  useEffect(() => {
    if (kunde?.status === 'lieferant') {
      loadLieferantBestellungen();
    }
  }, [kunde?.status, loadLieferantBestellungen]);

  const toggleProjekt = (projektId) => {
    setExpandedProjekte(prev => ({
      ...prev,
      [projektId]: !prev[projektId]
    }));
  };

  const getProjektStatusLabel = (status) => {
    switch (status) {
      case 'PLANUNG': return 'Planung';
      case 'IN_BEARBEITUNG': return 'In Bearbeitung';
      case 'ABGESCHLOSSEN': return 'Abgeschlossen';
      case 'ABGERECHNET': return 'Abgerechnet';
      default: return status;
    }
  };

  const getProjektStatusStyle = (status) => {
    switch (status) {
      case 'PLANUNG': return 'bg-blue-100 text-blue-700';
      case 'IN_BEARBEITUNG': return 'bg-yellow-100 text-yellow-700';
      case 'ABGESCHLOSSEN': return 'bg-green-100 text-green-700';
      case 'ABGERECHNET': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Unterschrift für Projekt speichern
  const handleSaveProjektUnterschrift = async (projektId, data) => {
    try {
      await unterschriftApi.create({
        projektId,
        ...data
      });
      setShowProjektSignaturePad(null);
      // Refresh trigger für die Liste
      setRefreshProjektUnterschriften(prev => ({
        ...prev,
        [projektId]: (prev[projektId] || 0) + 1
      }));
      setStatusMessage('Unterschrift erfolgreich gespeichert!');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Fehler beim Speichern der Unterschrift:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    }
  };

  // Anzahl offene/abgeschlossene Projekte
  const offeneProjekte = projekte?.filter(p =>
    p.status === 'PLANUNG' || p.status === 'IN_BEARBEITUNG'
  ).length || 0;

  const abgeschlosseneProjekte = projekte?.filter(p =>
    p.status === 'ABGESCHLOSSEN' || p.status === 'ABGERECHNET'
  ).length || 0;

  // Gefilterte Projekte basierend auf Suchbegriff und Status-Filter
  const filteredProjekte = useMemo(() => {
    if (!projekte) return [];

    let filtered = projekte;

    // Status-Filter anwenden
    if (projektStatusFilter === 'offen') {
      filtered = filtered.filter(p => p.status === 'PLANUNG' || p.status === 'IN_BEARBEITUNG');
    } else if (projektStatusFilter === 'erledigt') {
      filtered = filtered.filter(p => p.status === 'ABGESCHLOSSEN' || p.status === 'ABGERECHNET');
    }

    // Suchbegriff-Filter anwenden
    if (projektSearchTerm.trim()) {
      const searchLower = projektSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(projekt => {
        if (projekt.titel?.toLowerCase().includes(searchLower)) return true;
        if (projekt.nummer?.toLowerCase().includes(searchLower)) return true;
        if (projekt.beschreibung?.toLowerCase().includes(searchLower)) return true;
        if (getProjektStatusLabel(projekt.status)?.toLowerCase().includes(searchLower)) return true;
        return false;
      });
    }

    return filtered;
  }, [projekte, projektSearchTerm, projektStatusFilter]);

  const handleUpdateKunde = async (kundeData) => {
    setIsSaving(true);
    setError('');
    try {
      await updateKunde(id, kundeData);
      setIsModalOpen(false);
      await loadKunde();
    } catch (error) {
      console.error('Error updating kunde:', error);
      setError(error.response?.data?.error || 'Fehler beim Aktualisieren des Kunden');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteKunde = async () => {
    setIsDeleting(true);
    try {
      await deleteKunde(id);
      toast.success(`Kunde "${kunde.name}" wurde erfolgreich gelöscht.`);
      navigate('/kunden');
    } catch (error) {
      console.error('Error deleting kunde:', error);
      // Wenn Kunde bereits gelöscht (404), trotzdem zur Liste navigieren
      if (error.response?.status === 404) {
        navigate('/kunden');
        return;
      }
      setError(error.response?.data?.error || 'Fehler beim Löschen des Kunden');
      setIsDeleteModalOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCreateAnfrage = async (anfrageData) => {
    setIsAnfrageSaving(true);
    setAnfrageError('');
    try {
      await createAnfrage({
        ...anfrageData,
        kundeId: id
      });
      setIsAnfrageModalOpen(false);
      setEditingAnfrage(null);
      await loadKunde();
      setRefreshTimeline(prev => prev + 1);
      setStatusMessage('Anfrage erstellt');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error creating anfrage:', error);
      setAnfrageError(error.response?.data?.error || 'Fehler beim Erstellen der Anfrage');
    } finally {
      setIsAnfrageSaving(false);
    }
  };

  const handleUpdateAnfrage = async (anfrageData) => {
    setIsAnfrageSaving(true);
    setAnfrageError('');
    try {
      await updateAnfrage(editingAnfrage.id, anfrageData);
      setIsAnfrageModalOpen(false);
      setEditingAnfrage(null);
      await loadKunde();
      setRefreshTimeline(prev => prev + 1);
      setStatusMessage('Anfrage aktualisiert');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error updating anfrage:', error);
      setAnfrageError(error.response?.data?.error || 'Fehler beim Aktualisieren der Anfrage');
    } finally {
      setIsAnfrageSaving(false);
    }
  };

  // Aufmaß speichern (neu oder bearbeiten)
  const handleSaveAufmass = async (aufmass, isUpdate = false) => {
    if (!selectedAnfrageForAufmass) return;

    setIsAufmassSaving(true);
    try {
      // Aufmaß mit erstelltVon ergänzen (Benutzername aus Auth)
      const aufmassWithUser = {
        ...aufmass,
        erstelltVon: aufmass.erstelltVon || user?.name || 'Mitarbeiter'
      };

      if (isUpdate) {
        await updateAnfrage(selectedAnfrageForAufmass.id, { aufmassUpdate: aufmassWithUser });
        setStatusMessage('Aufmaß aktualisiert');
      } else {
        await updateAnfrage(selectedAnfrageForAufmass.id, { aufmass: aufmassWithUser });
        setStatusMessage('Aufmaß gespeichert');
      }

      setIsAufmassModalOpen(false);
      setSelectedAnfrageForAufmass(null);
      setEditingAufmass(null);
      await loadKunde();
      setRefreshTimeline(prev => prev + 1);
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error saving aufmass:', error);
      setAnfrageError(error.response?.data?.error || 'Fehler beim Speichern des Aufmaßes');
    } finally {
      setIsAufmassSaving(false);
    }
  };

  // Aufmaß löschen
  const handleDeleteAufmass = async (anfrage, aufmassId) => {
    if (!window.confirm('Möchten Sie dieses Aufmaß wirklich löschen?')) return;

    try {
      await updateAnfrage(anfrage.id, { aufmassDelete: aufmassId });
      await loadKunde();
      setRefreshTimeline(prev => prev + 1);
      setStatusMessage('Aufmaß gelöscht');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error deleting aufmass:', error);
      setAnfrageError(error.response?.data?.error || 'Fehler beim Löschen des Aufmaßes');
    }
  };

  const openAufmassModal = (anfrage, aufmassToEdit = null) => {
    setSelectedAnfrageForAufmass(anfrage);
    setEditingAufmass(aufmassToEdit);
    setIsAufmassModalOpen(true);
  };

  const handleZusammenfassung = async (anfrage) => {
    if (summarizingAnfrageId) return;
    setSummarizingAnfrageId(anfrage.id);
    try {
      const updated = await zusammenfassungAnfrage(anfrage.id);
      // Anfrage in der lokalen Liste aktualisieren
      setKunde(prev => ({
        ...prev,
        anfragen: prev.anfragen.map(a => a.id === updated.id ? { ...a, ...updated } : a)
      }));
    } catch (err) {
      console.error('Zusammenfassung fehlgeschlagen:', err);
      alert('Zusammenfassung konnte nicht erstellt werden: ' + (err.response?.data?.error || err.message));
    } finally {
      setSummarizingAnfrageId(null);
    }
  };

  const openEditAnfrageModal = (anfrage) => {
    setEditingAnfrage(anfrage);
    setAnfrageError('');
    setIsAnfrageModalOpen(true);
  };

  const openNewAnfrageModal = () => {
    setEditingAnfrage(null);
    setAnfrageError('');
    setIsAnfrageModalOpen(true);
  };

  const handleDeleteAnfrage = async () => {
    if (!editingAnfrage) return;
    setIsDeletingAnfrage(true);
    try {
      await deleteAnfrage(editingAnfrage.id);
      setIsDeleteAnfrageModalOpen(false);
      setIsAnfrageModalOpen(false);
      setEditingAnfrage(null);
      await loadKunde();
      setStatusMessage('Anfrage gelöscht');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error deleting anfrage:', error);
      setAnfrageError(error.response?.data?.error || 'Fehler beim Löschen der Anfrage');
      setIsDeleteAnfrageModalOpen(false);
    } finally {
      setIsDeletingAnfrage(false);
    }
  };

  const handleStatusUpdate = async (anfrageId, newStatus) => {
    try {
      await updateAnfrageStatus(anfrageId, newStatus);
      // Optimistic UI update
      setKunde(prevKunde => ({
        ...prevKunde,
        anfragen: prevKunde.anfragen.map(a =>
          a.id === anfrageId ? { ...a, status: newStatus } : a
        )
      }));
      // Timeline aktualisieren
      setRefreshTimeline(prev => prev + 1);
      setStatusMessage('Status aktualisiert');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusMessage('Fehler beim Aktualisieren');
      setTimeout(() => setStatusMessage(''), 2000);
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'neu': return 'Neu';
      case 'in_bearbeitung': return 'In Bearbeitung';
      case 'abgeschlossen': return 'Abgeschlossen';
      default: return status;
    }
  };

  const handleBulkDownload = async () => {
    try {
      setIsDownloading(true);
      // Download nur für den aktiven Tab (fotos oder dokumente)
      const blob = await downloadAllFiles(kunde.id, activeMediaTab);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const datum = new Date().toISOString().split('T')[0];
      const typeLabel = activeMediaTab === 'fotos' ? 'Fotos' : 'Dokumente';
      link.download = `${kunde.name.replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '').replace(/\s+/g, '_')}_${typeLabel}_${datum}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download Fehler:', error);
      alert(error.response?.data?.error || 'Fehler beim Herunterladen der Dateien');
    } finally {
      setIsDownloading(false);
    }
  };

  // Anzahl der Dateien im aktiven Tab
  const activeTabFileCount = activeMediaTab === 'fotos' ? (fotos?.length || 0) : (dokumente?.length || 0);

  // Unterschrift speichern
  const handleSaveUnterschrift = async (anfrageId, data) => {
    try {
      await unterschriftApi.create({
        anfrageId,
        ...data
      });
      setShowSignaturePad(null);
      // Refresh trigger für die Liste
      setRefreshUnterschriften(prev => ({
        ...prev,
        [anfrageId]: (prev[anfrageId] || 0) + 1
      }));
      setStatusMessage('Unterschrift erfolgreich gespeichert!');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Fehler beim Speichern der Unterschrift:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    }
  };

  // Skizze speichern (als Foto in Kundenfotos)
  const handleSaveSkizze = async (anfrageId, file) => {
    setIsSkizzeSaving(true);
    try {
      await uploadFoto(id, file, 'Skizze', anfrageId);
      setShowSkizzenPad(null);
      // Fotos + Kunde (Anfragen inkl. Fotos) neu laden
      const [fotosData] = await Promise.all([
        getFotosByKundeId(id),
        loadKunde()
      ]);
      setFotos(fotosData);
      setRefreshTimeline(prev => prev + 1);
      setStatusMessage('Skizze gespeichert!');
      setTimeout(() => setStatusMessage(''), 2000);
    } catch (error) {
      console.error('Fehler beim Speichern der Skizze:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    } finally {
      setIsSkizzeSaving(false);
    }
  };

  const offeneAnfragen = kunde?.anfragen?.filter(a =>
    a.status === 'neu' || a.status === 'in_bearbeitung'
  ).length || 0;

  const abgeschlosseneAnfragen = kunde?.anfragen?.filter(a =>
    a.status === 'abgeschlossen'
  ).length || 0;

  // Gefilterte Anfragen basierend auf Suchbegriff und Status-Filter
  const filteredAnfragen = useMemo(() => {
    if (!kunde?.anfragen) return [];

    let filtered = kunde.anfragen;

    // Status-Filter anwenden
    if (anfrageStatusFilter === 'offen') {
      filtered = filtered.filter(a => a.status === 'neu' || a.status === 'in_bearbeitung');
    } else if (anfrageStatusFilter === 'erledigt') {
      filtered = filtered.filter(a => a.status === 'abgeschlossen');
    }

    // Suchbegriff-Filter anwenden
    if (anfrageSearchTerm.trim()) {
      const searchLower = anfrageSearchTerm.toLowerCase().trim();
      filtered = filtered.filter(anfrage => {
        // Suche in Betreff
        if (anfrage.betreff?.toLowerCase().includes(searchLower)) return true;
        // Suche in Nachricht
        if (anfrage.nachricht?.toLowerCase().includes(searchLower)) return true;
        // Suche in Kategorie
        if (anfrage.kategorie?.toLowerCase().includes(searchLower)) return true;
        // Suche in Status
        if (getStatusLabel(anfrage.status)?.toLowerCase().includes(searchLower)) return true;
        // Suche in extrahierten Daten
        if (anfrage.extrahierteDaten) {
          const extrahierteString = JSON.stringify(anfrage.extrahierteDaten).toLowerCase();
          if (extrahierteString.includes(searchLower)) return true;
        }
        return false;
      });
    }

    return filtered;
  }, [kunde?.anfragen, anfrageSearchTerm, anfrageStatusFilter]);

  // Pagination: Anfragen seitenweise anzeigen
  const anfragenTotalPages = Math.ceil(filteredAnfragen.length / ITEMS_PER_PAGE);
  const paginatedAnfragen = useMemo(() => {
    const start = anfragenPage * ITEMS_PER_PAGE;
    return filteredAnfragen.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAnfragen, anfragenPage]);

  // Pagination: Termine seitenweise anzeigen
  const termineList = kunde?.termine || [];
  const termineTotalPages = Math.ceil(termineList.length / ITEMS_PER_PAGE);
  const paginatedTermine = useMemo(() => {
    const start = terminePage * ITEMS_PER_PAGE;
    return termineList.slice(start, start + ITEMS_PER_PAGE);
  }, [termineList, terminePage]);

  // Reset Anfragen-Seite wenn Filter sich ändern
  useEffect(() => { setAnfragenPage(0); }, [anfrageSearchTerm, anfrageStatusFilter]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8 flex justify-center items-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!kunde) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-8">
          <div className="bg-surface rounded-card shadow-card p-8 text-center">
            <p className="text-text-muted">Kunde nicht gefunden</p>
            <Link to="/kunden" className="text-primary hover:underline mt-4 inline-block">
              Zurück zur Kundenliste
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 overflow-x-hidden min-h-screen"
        initial={{ opacity: 0 }}
        animate={{ opacity: isPageReady ? 1 : 0 }}
        transition={{ duration: 0.1 }}
      >
        {/* Zurück Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isContentVisible ? 1 : 0 }}
          transition={{ duration: 0.15 }}
        >
          {kunde.hauptLieferantInfo ? (
            <Link
              to={`/kunden/${kunde.hauptLieferantInfo.id}`}
              className="inline-flex items-center gap-2 text-text-muted hover:text-text mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zu {kunde.hauptLieferantInfo.name}
            </Link>
          ) : (
            <Link
              to="/kunden"
              className="inline-flex items-center gap-2 text-text-muted hover:text-text mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zur Kundenliste
            </Link>
          )}
        </motion.div>

        {/* Kunden-Header Card - always visible first */}
        <motion.div
          className="bg-surface rounded-card shadow-card p-4 sm:p-6 mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: isPageReady ? 1 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <div className="flex justify-between items-start mb-4 gap-2">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <h1 className="text-xl sm:text-3xl font-serif font-bold truncate">{kunde.name}</h1>
              {kunde.kundennummer && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(kunde.kundennummer);
                    toast.success(`Kundennummer ${kunde.kundennummer} kopiert`);
                  }}
                  className="shrink-0 text-xs sm:text-sm font-mono px-2 py-0.5 rounded bg-gray-100 hover:bg-gray-200 text-text-muted hover:text-text transition"
                  title="Kundennummer in Zwischenablage kopieren"
                >
                  #{kunde.kundennummer}
                </button>
              )}
              {kunde.kundenTyp === 'HAUSVERWALTUNG' && (
                <span className="shrink-0 text-[10px] sm:text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Hausverwaltung</span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: isContentVisible ? 1 : 0 }}
                transition={{ duration: 0.15 }}
              >
                <Link
                  to={`/kostenvoranschlaege/neu?kundeId=${id}`}
                  className="flex items-center gap-1 sm:gap-2 bg-primary-light text-primary px-2 sm:px-4 py-1.5 sm:py-2 rounded-button font-medium hover:bg-primary hover:text-white transition text-sm sm:text-base"
                >
                  <ClipboardList className="w-4 h-4" />
                  <span className="hidden sm:inline">KVA erstellen</span>
                </Link>
              </motion.div>
              <motion.button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center gap-1 sm:gap-2 bg-primary text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-button font-medium hover:bg-primary-dark transition shrink-0 text-sm sm:text-base"
                initial={{ opacity: 0 }}
                animate={{ opacity: isContentVisible ? 1 : 0 }}
                transition={{ duration: 0.15 }}
              >
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Bearbeiten</span>
              </motion.button>
            </div>
          </div>
          <div className="flex justify-between items-start">
            <div>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-text-muted">
                  <Mail className="w-4 h-4" />
                  {kunde.email && (kunde.email.endsWith('@temp.local') || kunde.email.endsWith('@import.local')) ? (
                    <span className="italic text-text-muted/70">Keine E-Mail hinterlegt</span>
                  ) : (
                    <a href={`mailto:${kunde.email}`} className="hover:text-primary">
                      {kunde.email}
                    </a>
                  )}
                </div>
                {kunde.telefon && (
                  <div className="flex items-center gap-2 text-text-muted">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${kunde.telefon}`} className="hover:text-primary">
                      {kunde.telefon}
                    </a>
                  </div>
                )}
                {/* Sekundärnummern (Multi-Tel) — werden von Kiki beim Caller-ID-
                    Lookup zusätzlich konsultiert. Bewusst direkt unter der
                    Hauptnummer, damit Disponent das Konzept „weitere Nummern
                    dieses Kunden" sofort erkennt. */}
                {(kunde.zusatzTelefon?.length > 0 || zusatzAddOpen) && (
                  <div className="ml-6 space-y-1">
                    {(kunde.zusatzTelefon || []).map(t => (
                      <div key={t.id} className="flex items-center gap-2 text-text-muted text-sm group">
                        <Phone className="w-3.5 h-3.5 opacity-60" />
                        <a href={`tel:${t.nummer}`} className="hover:text-primary">{t.nummer}</a>
                        {t.label && <span className="text-xs text-text-muted/70 italic">({t.label})</span>}
                        <button
                          type="button"
                          onClick={() => handleDeleteZusatzTelefon(t.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-text-muted hover:text-red-600 ml-1"
                          title="Nummer entfernen"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    {zusatzAddOpen && (
                      <div className="flex items-center gap-2 pt-1">
                        <Phone className="w-3.5 h-3.5 opacity-60" />
                        <input
                          type="tel"
                          value={neueZusatzNummer}
                          onChange={(e) => setNeueZusatzNummer(e.target.value)}
                          placeholder="Telefonnummer"
                          className="text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:border-primary"
                          autoFocus
                          onKeyDown={(e) => e.key === 'Enter' && handleAddZusatzTelefon()}
                        />
                        <input
                          type="text"
                          value={neueZusatzLabel}
                          onChange={(e) => setNeueZusatzLabel(e.target.value)}
                          placeholder="Label (optional)"
                          className="text-sm border border-gray-300 rounded px-2 py-0.5 w-32 focus:outline-none focus:border-primary"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddZusatzTelefon()}
                        />
                        <button
                          type="button"
                          onClick={handleAddZusatzTelefon}
                          disabled={zusatzSaving || !neueZusatzNummer.trim()}
                          className="text-sm text-primary hover:text-primary-dark disabled:opacity-50"
                        >
                          Speichern
                        </button>
                        <button
                          type="button"
                          onClick={() => { setZusatzAddOpen(false); setNeueZusatzNummer(''); setNeueZusatzLabel(''); }}
                          className="text-sm text-text-muted hover:text-text"
                        >
                          Abbrechen
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!zusatzAddOpen && (
                  <button
                    type="button"
                    onClick={() => setZusatzAddOpen(true)}
                    className="ml-6 text-xs text-text-muted hover:text-primary flex items-center gap-1 mt-0.5"
                  >
                    <Plus className="w-3 h-3" />
                    weitere Nummer hinzufügen
                  </button>
                )}
                {kunde.adresse && (
                  <div className="flex items-center gap-2 text-text-muted relative">
                    <MapPin className="w-4 h-4" />
                    <a
                      ref={addressRef}
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(kunde.adresse)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-primary"
                      onMouseEnter={handleAddressMouseEnter}
                      onMouseLeave={handleAddressMouseLeave}
                    >
                      {kunde.adresse}
                    </a>
                    {formattedDistance && (
                      <span className="text-xs px-2 py-0.5 bg-primary-light text-primary rounded-full font-medium">
                        {formattedDistance} · {formattedDriveTime}
                      </span>
                    )}
                    {distanceLoading && (
                      <span className="text-xs text-text-muted">
                        <span className="inline-block w-3 h-3 border border-gray-300 border-t-primary rounded-full animate-spin"></span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              kunde.status === 'stammkunde'
                ? 'bg-primary-light text-primary'
                : kunde.status === 'lieferant'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-blue-100 text-blue-700'
            }`}>
              {kunde.status === 'stammkunde' ? 'Stammkunde' : kunde.status === 'lieferant' ? 'Lieferant' : 'Neukunde'}
            </span>
          </div>

          {kunde.info && (
            <div className="mt-4 pt-4 border-t border-border">
              <h3 className="font-semibold mb-2">Notizen</h3>
              <p className="text-text-muted whitespace-pre-wrap">{kunde.info}</p>
            </div>
          )}

        </motion.div>

        {/* Ansprechpartner + Alle Anfragen für Haupt-Lieferanten */}
        {kunde.status === 'lieferant' && !kunde.hauptLieferantId && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6 mb-6"
            variants={itemVariants}
          >
            {/* Ansprechpartner-Karten */}
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2 justify-between">
              <span className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-600" />
                Ansprechpartner ({kunde.ansprechpartner?.length || 0})
              </span>
              <button
                onClick={() => setIsApModalOpen(true)}
                className="flex items-center gap-1 bg-amber-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-700 transition"
              >
                <Plus className="w-4 h-4" />
                Neuer Ansprechpartner
              </button>
            </h3>
            {kunde.ansprechpartner?.length > 0 && <div className="flex flex-wrap gap-3 mb-6">
              {kunde.ansprechpartner.map(ap => (
                <Link
                  key={ap.id}
                  to={`/kunden/${ap.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-background transition group"
                >
                  <div className="w-7 h-7 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-semibold text-xs">
                    {ap.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{ap.name}</div>
                    <div className="text-xs text-text-muted">{ap.email}</div>
                  </div>
                  {ap.anfragen?.length > 0 && (
                    <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full text-xs ml-1">
                      {ap.anfragen.length}
                    </span>
                  )}
                </Link>
              ))}
            </div>}

            {/* Alle Anfragen konsolidiert + filterbar */}
            {kunde.ansprechpartner?.some(ap => ap.anfragen?.length > 0) && (() => {
              const alleAnfragen = kunde.ansprechpartner.flatMap(ap =>
                (ap.anfragen || []).map(a => ({
                  ...a,
                  apName: ap.name,
                  apId: ap.id
                }))
              ).sort((a, b) => new Date(b.erstelltAm) - new Date(a.erstelltAm));

              const gesamtDokumente = alleAnfragen.reduce((sum, a) => sum + (a.dokumente?.length || 0), 0);

              return (
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm">
                      Alle Anfragen ({alleAnfragen.length})
                      {gesamtDokumente > 0 && (
                        <span className="text-text-muted font-normal ml-2">· {gesamtDokumente} Dokumente</span>
                      )}
                    </h4>
                    {/* Filter-Buttons nach Ansprechpartner */}
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => setLieferantApFilter && setLieferantApFilter(null)}
                        className={`px-2 py-0.5 rounded-full text-xs transition cursor-pointer ${
                          !lieferantApFilter
                            ? 'bg-amber-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        Alle
                      </button>
                      {kunde.ansprechpartner.filter(ap => ap.anfragen?.length > 0).map(ap => (
                        <button
                          key={ap.id}
                          onClick={() => setLieferantApFilter && setLieferantApFilter(lieferantApFilter === ap.id ? null : ap.id)}
                          className={`px-2 py-0.5 rounded-full text-xs transition cursor-pointer ${
                            lieferantApFilter === ap.id
                              ? 'bg-amber-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {ap.name?.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {alleAnfragen
                      .filter(a => !lieferantApFilter || a.apId === lieferantApFilter)
                      .map(a => (
                      <Link
                        key={a.id}
                        to={`/kunden/${a.apId}`}
                        className="flex items-center justify-between p-2.5 rounded-lg border border-border hover:bg-background transition text-sm group"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${a.status === 'neu' ? 'bg-blue-500' : a.status === 'abgeschlossen' ? 'bg-gray-400' : 'bg-green-500'}`} />
                          <span className="font-medium truncate">{a.betreff}</span>
                          <span className="text-amber-600 text-xs flex-shrink-0 bg-amber-50 px-1.5 py-0.5 rounded">
                            {a.apName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 text-xs text-text-muted">
                          {(a.dokumente?.length || 0) > 0 && (
                            <span className="flex items-center gap-1 text-purple-600">
                              <FileText className="w-3 h-3" />{a.dokumente.length}
                            </span>
                          )}
                          {(a.fotos?.length || 0) > 0 && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <ImageIcon className="w-3 h-3" />{a.fotos.length}
                            </span>
                          )}
                          <span>{new Date(a.erstelltAm).toLocaleDateString('de-DE')}</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })()}
          </motion.div>
        )}

        {/* Haupt-Lieferant Link für Ansprechpartner */}
        {kunde.status === 'lieferant' && kunde.hauptLieferantInfo && (
          <motion.div
            className="bg-amber-50 border border-amber-200 rounded-card p-4 mb-6"
            variants={itemVariants}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-amber-600">🏢</span>
                <span className="text-text-muted">Ansprechpartner von</span>
                <Link to={`/kunden/${kunde.hauptLieferantInfo.id}`} className="font-semibold text-amber-700 hover:underline">
                  {kunde.hauptLieferantInfo.name}
                </Link>
              </div>
              <Link to={`/kunden/${kunde.hauptLieferantInfo.id}`} className="text-amber-600 hover:text-amber-800">
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}

        {/* Bestellhistorie für Lieferanten */}
        {kunde.status === 'lieferant' && lieferantBestellungen.length > 0 && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-6 mb-6"
            variants={itemVariants}
          >
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <span className="text-amber-600">📦</span>
              Bestellhistorie ({lieferantBestellungen.length})
            </h3>
            <div className="space-y-2">
              {lieferantBestellungen.map(b => {
                const statusConfig = {
                  ENTWURF: { label: 'Entwurf', cls: 'bg-gray-100 text-gray-700' },
                  VERSENDET: { label: 'Versendet', cls: 'bg-blue-100 text-blue-700' },
                  BESTAETIGT: { label: 'Bestätigt', cls: 'bg-emerald-100 text-emerald-700' },
                  TEILGELIEFERT: { label: 'Teilgeliefert', cls: 'bg-amber-100 text-amber-700' },
                  VOLLSTAENDIG_GELIEFERT: { label: 'Geliefert', cls: 'bg-green-100 text-green-700' },
                  STORNIERT: { label: 'Storniert', cls: 'bg-red-100 text-red-700' }
                };
                const sc = statusConfig[b.status] || { label: b.status, cls: 'bg-gray-100 text-gray-700' };
                return (
                  <Link
                    key={b.id}
                    to={`/projekte/${b.projektId}`}
                    className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-background transition group"
                  >
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sc.cls}`}>
                        {sc.label}
                      </span>
                      <div>
                        <div className="font-medium text-sm">{b.referenzNummer || 'Entwurf'}</div>
                        <div className="text-xs text-text-muted">
                          {b.projekt?.titel || ''} · {b.positionen?.length || 0} Pos. · {new Date(b.erstelltAm).toLocaleDateString('de-DE')}
                        </div>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition" />
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Status Toast */}
        {statusMessage && (
          <div className="fixed bottom-6 right-6 bg-primary text-white px-4 py-2 rounded-lg shadow-lg z-50">
            {statusMessage}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Anfragen & Projekte Box - slides from left */}
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, x: -15 }}
            animate={{
              opacity: isContentVisible ? 1 : 0,
              x: isContentVisible ? 0 : -15
            }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            {/* Tab-Header */}
            <div className="flex border-b border-border mb-4">
              <button
                onClick={() => setActiveAnfragenProjekteTab('anfragen')}
                className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition ${
                  activeAnfragenProjekteTab === 'anfragen'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                Anfragen ({kunde.anfragen?.length || 0})
              </button>
              <button
                onClick={() => setActiveAnfragenProjekteTab('projekte')}
                className={`flex items-center gap-2 px-4 py-2 font-medium text-sm border-b-2 transition ${
                  activeAnfragenProjekteTab === 'projekte'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >
                <FolderKanban className="w-4 h-4" />
                Projekte ({projekte?.length || 0})
              </button>
            </div>

            {/* Anfragen Tab */}
            <div className={activeAnfragenProjekteTab === 'anfragen' ? 'block' : 'hidden'}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={() => setAnfrageStatusFilter(anfrageStatusFilter === 'offen' ? null : 'offen')}
                    className={`px-2 py-1 rounded-full cursor-pointer transition-all ${
                      anfrageStatusFilter === 'offen'
                        ? 'bg-blue-500 text-white ring-2 ring-blue-300'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    }`}
                  >
                    {offeneAnfragen} offen
                  </button>
                  <button
                    onClick={() => setAnfrageStatusFilter(anfrageStatusFilter === 'erledigt' ? null : 'erledigt')}
                    className={`px-2 py-1 rounded-full cursor-pointer transition-all ${
                      anfrageStatusFilter === 'erledigt'
                        ? 'bg-green-500 text-white ring-2 ring-green-300'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {abgeschlosseneAnfragen} erledigt
                  </button>
                </div>
              </div>
              <button
                onClick={openNewAnfrageModal}
                className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-4 h-4" />
                Neue Anfrage
              </button>
            </div>

            {/* Suchleiste für Anfragen */}
            {kunde.anfragen && kunde.anfragen.length > 0 && (
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="text"
                    value={anfrageSearchTerm}
                    onChange={(e) => setAnfrageSearchTerm(e.target.value)}
                    placeholder="Anfragen durchsuchen..."
                    className="w-full pl-10 pr-10 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  {anfrageSearchTerm && (
                    <button
                      onClick={() => setAnfrageSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
                    >
                      <X className="w-4 h-4 text-text-muted" />
                    </button>
                  )}
                </div>
                {(anfrageSearchTerm || anfrageStatusFilter) && (
                  <p className="text-xs text-text-muted mt-2">
                    {filteredAnfragen.length} von {kunde.anfragen.length} Anfragen
                    {anfrageStatusFilter && ` (${anfrageStatusFilter === 'offen' ? 'offen' : 'erledigt'})`}
                  </p>
                )}
              </div>
            )}

            {kunde.anfragen && kunde.anfragen.length > 0 ? (
              filteredAnfragen.length > 0 ? (
              <div className="space-y-3">
                {paginatedAnfragen.map((anfrage) => {
                  const isExpanded = expandedAnfragen[anfrage.id];
                  return (
                    <div key={anfrage.id} ref={el => { anfrageRefs.current[anfrage.id] = el; }} className="border border-border rounded-lg overflow-hidden">
                      {/* Klickbarer Header */}
                      <button
                        onClick={() => toggleAnfrage(anfrage.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition text-left"
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
                            anfrage.status === 'neu' ? 'bg-blue-100 text-blue-700' :
                            anfrage.status === 'in_bearbeitung' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {getStatusLabel(anfrage.status)}
                          </span>
                          <h3 className="font-medium truncate">{anfrage.betreff}</h3>
                          {anfrage.objekt && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 flex items-center gap-1 shrink-0">
                              <Building2 className="w-3 h-3" />
                              <span className="truncate max-w-[120px]">{anfrage.objekt.name || anfrage.objekt.strasse}</span>
                            </span>
                          )}
                          {anfrage.fotos && anfrage.fotos.length > 0 && (
                            <span className="text-xs text-text-muted flex items-center gap-1 shrink-0">
                              <ImageIcon className="w-3 h-3" />
                              {anfrage.fotos.length}
                            </span>
                          )}
                          {anfrage.dokumente && anfrage.dokumente.length > 0 && (
                            <span className="text-xs text-text-muted flex items-center gap-1 shrink-0">
                              <FileText className="w-3 h-3" />
                              {anfrage.dokumente.length}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs text-text-muted hidden sm:block">
                            {formatDate(anfrage.erstelltAm)}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5 text-text-muted" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-text-muted" />
                          )}
                        </div>
                      </button>

                      {/* Ausgeklappter Inhalt */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-border bg-gray-50/50">
                          <div className="pt-4">
                            {/* Aktions-Buttons */}
                            <div className="flex flex-wrap justify-end gap-2 sm:gap-4 mb-3">
                              {anfrage.nachricht && anfrage.nachricht.length > 500 && (
                                <button
                                  onClick={() => handleZusammenfassung(anfrage)}
                                  disabled={summarizingAnfrageId === anfrage.id}
                                  className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-purple-600 hover:text-purple-800 sm:bg-transparent bg-purple-50 rounded-lg sm:rounded-none transition disabled:opacity-50"
                                >
                                  {summarizingAnfrageId === anfrage.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                  <span>{summarizingAnfrageId === anfrage.id ? 'Fasst zusammen...' : 'KI Zusammenfassung'}</span>
                                </button>
                              )}
                              <button
                                onClick={() => openAufmassModal(anfrage)}
                                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-primary hover:text-primary-dark sm:bg-transparent bg-primary-light rounded-lg sm:rounded-none transition"
                              >
                                <Ruler className="w-4 h-4" />
                                <span>Aufmaß</span>
                              </button>
                              <button
                                onClick={() => setShowSkizzenPad({ anfrageId: anfrage.id })}
                                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-primary hover:text-primary-dark sm:bg-transparent bg-primary-light rounded-lg sm:rounded-none transition"
                              >
                                <PenTool className="w-4 h-4" />
                                <span>Skizze</span>
                              </button>
                              <Link
                                to={`/kostenvoranschlaege/neu?kundeId=${id}&anfrageId=${anfrage.id}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-primary hover:text-primary-dark sm:bg-transparent bg-primary-light rounded-lg sm:rounded-none transition"
                              >
                                <ClipboardList className="w-4 h-4" />
                                <span>KVA</span>
                              </Link>
                              <button
                                onClick={() => openEditAnfrageModal(anfrage)}
                                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-text-muted hover:text-primary sm:bg-transparent bg-gray-100 rounded-lg sm:rounded-none transition"
                              >
                                <Pencil className="w-4 h-4" />
                                <span>Bearbeiten</span>
                              </button>
                              <Link
                                to={`/anfragen/${anfrage.id}`}
                                className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-text-muted hover:text-primary sm:bg-transparent bg-gray-100 rounded-lg sm:rounded-none transition"
                              >
                                <ExternalLink className="w-4 h-4" />
                                <span>Detailansicht</span>
                              </Link>
                            </div>

                            {/* Verknüpftes Objekt */}
                            {anfrage.objekt && (
                              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
                                <p className="text-xs text-purple-700 font-medium mb-1 flex items-center gap-1">
                                  <Building2 className="w-3 h-3" />
                                  Verknüpftes Objekt
                                </p>
                                <p className="text-sm font-medium text-purple-900">
                                  {anfrage.objekt.name || anfrage.objekt.strasse}
                                </p>
                                {anfrage.objekt.strasse && anfrage.objekt.name && (
                                  <p className="text-xs text-purple-600">{anfrage.objekt.strasse}{anfrage.objekt.ort ? `, ${anfrage.objekt.ort}` : ''}</p>
                                )}
                                {!anfrage.objekt.name && anfrage.objekt.ort && (
                                  <p className="text-xs text-purple-600">{anfrage.objekt.ort}</p>
                                )}
                              </div>
                            )}

                            {/* Nachricht */}
                            <div className="mb-4">
                              <p className="text-xs text-text-muted mb-1">Nachricht:</p>
                              <p className="text-sm whitespace-pre-wrap">{anfrage.nachricht}</p>
                            </div>

                            {/* Extrahierte Daten - DYNAMISCH für alle Pflichtfelder (Multi-Tenancy!) */}
                            {anfrage.extrahierteDaten && Object.keys(anfrage.extrahierteDaten).length > 0 && (
                              <div className="mb-4 p-3 bg-primary-light rounded-lg">
                                <p className="text-xs text-primary font-medium mb-2">Extrahierte Details:</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  {Object.entries(anfrage.extrahierteDaten)
                                    // pflichtfelder-Objekt flach auflösen
                                    .flatMap(([key, value]) => {
                                      if (key === 'pflichtfelder' && typeof value === 'object' && value !== null && !Array.isArray(value)) {
                                        return Object.entries(value);
                                      }
                                      return [[key, value]];
                                    })
                                    .filter(([key, value]) => {
                                      // Technische Felder ausschließen
                                      const excludeKeys = ['gewaehlterTermin', 'wunschtermin', 'preisanfrage', 'dringend', 'vorgeschlageneTermine', 'aufmasse', 'dokumentAnfrage', 'arbeitsnachweise', 'letzterArbeitsnachweis', 'gesamtStunden'];
                                      if (excludeKeys.includes(key)) return false;
                                      // HeyKiki-Endkunden sehen keine Telefonassistent-Provenance-Felder
                                      if (isHeykikiOrg && ['quelle', 'source', 'agentId', 'agent_id', 'conversationId', 'conversation_id', 'callSuccessful', 'call_successful', 'callId'].includes(key)) return false;
                                      // Leere Werte ausschließen
                                      if (value === null || value === undefined || value === '' || value === false) return false;
                                      // Objekte nur wenn sie Inhalt haben
                                      if (typeof value === 'object' && Object.keys(value).length === 0) return false;
                                      return true;
                                    })
                                    .map(([key, value]) => {
                                      // Label formatieren: snake_case/camelCase -> Title Case,
                                      // Acronyme (KVA, PLZ, ID, UST, URL, SMS, PDF, KFZ)
                                      // bleiben groß — sonst wird „kvaPositionen" zu „Kva Positionen".
                                      const ACRONYMS = new Set(['KVA', 'PLZ', 'ID', 'UST', 'URL', 'SMS', 'PDF', 'KFZ']);
                                      const formatLabel = (k) => k
                                        .replace(/_/g, ' ')
                                        .replace(/([a-z])([A-Z])/g, '$1 $2')
                                        .split(' ')
                                        .filter(Boolean)
                                        .map(word => {
                                          const upper = word.toUpperCase();
                                          if (ACRONYMS.has(upper)) return upper;
                                          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                                        })
                                        .join(' ')
                                        .trim();
                                      // Einzelnes Objekt-Item einer Liste als Zeile rendern.
                                      // Erkennt KVA-Positionen-Style { menge, einheit, artikel } und
                                      // Material-Style { menge, einheit, bezeichnung }; fällt sonst
                                      // auf „key: value · key: value" zurück.
                                      const formatObjectLine = (item) => {
                                        if (item === null || typeof item !== 'object') return String(item);
                                        const mengeParts = [item.menge, item.einheit]
                                          .filter(v => v !== null && v !== undefined && v !== '');
                                        const mengeText = mengeParts.join(' ');
                                        const name = item.artikel || item.bezeichnung || item.name || item.titel;
                                        if (name) return mengeText ? `${mengeText} — ${name}` : String(name);
                                        return Object.entries(item)
                                          .filter(([, v]) => v !== null && v !== undefined && v !== '')
                                          .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
                                          .join(' · ');
                                      };
                                      // Wert formatieren. Arrays-of-Objects werden als Liste
                                      // gerendert (z. B. kvaPositionen). Einfache Arrays werden
                                      // kommagetrennt, flache Objekte als Key-Value-Pairs.
                                      const formatValue = (v) => {
                                        if (v === null || v === undefined) return '—';
                                        if (typeof v === 'boolean') return v ? 'Ja' : 'Nein';
                                        if (typeof v === 'string' || typeof v === 'number') return String(v);
                                        if (Array.isArray(v)) {
                                          if (v.length === 0) return '—';
                                          const hasObjects = v.some(x => x !== null && typeof x === 'object');
                                          if (!hasObjects) return v.filter(x => x !== null && x !== undefined && x !== '').join(', ');
                                          return (
                                            <ul className="list-disc list-inside space-y-0.5 mt-1 ml-1">
                                              {v.map((item, i) => (
                                                <li key={i} className="text-sm font-normal">{formatObjectLine(item)}</li>
                                              ))}
                                            </ul>
                                          );
                                        }
                                        if (typeof v === 'object') {
                                          const entries = Object.entries(v).filter(([, val]) => val !== null && val !== undefined && val !== '');
                                          if (entries.length === 0) return '—';
                                          return entries.map(([k, val]) => `${formatLabel(k)}: ${typeof val === 'object' ? JSON.stringify(val) : val}`).join(' · ');
                                        }
                                        return String(v);
                                      };
                                      // Arrays/Objects + lange Strings über 2 Spalten — sonst
                                      // werden Listen im schmalen Col unleserlich umgebrochen.
                                      const isStructured = Array.isArray(value) || (typeof value === 'object' && value !== null);
                                      const isLongValue = isStructured
                                        || ['adresse', 'beschreibung', 'problem'].includes(key)
                                        || (typeof value === 'string' && value.length > 30);
                                      const formatted = formatValue(value);
                                      return (
                                        <div key={key} className={isLongValue ? 'col-span-2' : ''}>
                                          <span className="text-text-muted">{formatLabel(key)}: </span>
                                          <span className="font-medium">{formatted}</span>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>
                            )}

                            {/* Aufmaße */}
                            {anfrage.extrahierteDaten?.aufmasse && anfrage.extrahierteDaten.aufmasse.length > 0 && (
                              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                <p className="text-xs text-blue-700 font-medium mb-2 flex items-center gap-1">
                                  <Ruler className="w-3 h-3" />
                                  Aufmaße ({anfrage.extrahierteDaten.aufmasse.length})
                                </p>
                                <div className="space-y-2">
                                  {anfrage.extrahierteDaten.aufmasse.map((aufmass, idx) => (
                                    <div key={aufmass.id || idx} className="bg-white p-2 rounded border border-blue-100">
                                      <div className="flex items-center justify-between gap-2">
                                        <span className="font-medium text-sm">{aufmass.bezeichnung}</span>
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500">
                                            {aufmass.breite} × {aufmass.hoehe}{aufmass.tiefe ? ` × ${aufmass.tiefe}` : ''} {aufmass.einheit}
                                          </span>
                                          <button
                                            onClick={() => openAufmassModal(anfrage, aufmass)}
                                            className="p-1 text-gray-400 hover:text-primary transition"
                                            title="Bearbeiten"
                                          >
                                            <Pencil className="w-3.5 h-3.5" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteAufmass(anfrage, aufmass.id)}
                                            className="p-1 text-gray-400 hover:text-danger transition"
                                            title="Löschen"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                      </div>
                                      {(aufmass.material || aufmass.notizen || (aufmass.customFields && aufmass.customFields.length > 0)) && (
                                        <div className="text-xs text-gray-600 mt-1">
                                          {aufmass.material && <span className="mr-3">{aufmass.material}</span>}
                                          {aufmass.notizen && <span className="italic mr-3">{aufmass.notizen}</span>}
                                          {aufmass.customFields && aufmass.customFields.map((field, i) => (
                                            <span key={i} className="mr-3">{field.name}: {field.value}</span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Arbeitsnachweise */}
                            {anfrage.extrahierteDaten?.arbeitsnachweise && anfrage.extrahierteDaten.arbeitsnachweise.length > 0 && (
                              <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                                <p className="text-xs text-amber-700 font-medium mb-2 flex items-center gap-1">
                                  <ClipboardList className="w-3 h-3" />
                                  Arbeitsnachweise ({anfrage.extrahierteDaten.arbeitsnachweise.length})
                                  {anfrage.extrahierteDaten.gesamtStunden && (
                                    <span className="ml-2 text-amber-600">
                                      — Gesamt: {anfrage.extrahierteDaten.gesamtStunden} Std.
                                    </span>
                                  )}
                                </p>
                                <div className="space-y-2">
                                  {anfrage.extrahierteDaten.arbeitsnachweise.map((nachweis, idx) => (
                                    <div key={idx} className="bg-white p-2.5 rounded border border-amber-100">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <span className="font-medium text-sm">
                                            {nachweis.taetigkeit || nachweis.notiz || nachweis.eingabe || 'Arbeitsnachweis'}
                                          </span>
                                          {nachweis.stunden && (
                                            <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">
                                              {nachweis.stunden} Std.
                                            </span>
                                          )}
                                        </div>
                                        {nachweis.datum && (
                                          <span className="text-xs text-gray-500 whitespace-nowrap">
                                            {new Date(nachweis.datum).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                          </span>
                                        )}
                                      </div>
                                      {nachweis.material && nachweis.material.length > 0 && (
                                        <div className="mt-1.5 text-xs text-gray-600">
                                          <span className="text-gray-400">Material: </span>
                                          {nachweis.material.map((m, i) => (
                                            <span key={i}>
                                              {i > 0 && ', '}
                                              {m.menge} {m.einheit} {m.bezeichnung}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      {nachweis.notiz && nachweis.taetigkeit && (
                                        <p className="mt-1 text-xs text-gray-500 italic">{nachweis.notiz}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Kategorie & Datum */}
                            <div className="flex flex-wrap gap-2 mb-4">
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                {anfrage.kategorie}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                {formatDateTime(anfrage.erstelltAm)}
                              </span>
                            </div>

                            {/* Fotos der Anfrage */}
                            {anfrage.fotos && anfrage.fotos.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs text-text-muted mb-2 flex items-center gap-1">
                                  <ImageIcon className="w-3 h-3" />
                                  {anfrage.fotos.length} {anfrage.fotos.length === 1 ? 'Foto' : 'Fotos'}
                                </p>
                                <div className="flex gap-2 flex-wrap">
                                  {anfrage.fotos.map((foto) => (
                                    <div key={foto.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-border hover:border-primary transition group/foto">
                                      <a
                                        href={getFotoUrl(foto)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block w-full h-full"
                                      >
                                        <img
                                          src={getFotoUrl(foto)}
                                          alt={foto.beschreibung || 'Foto'}
                                          className="w-full h-full object-cover"
                                        />
                                      </a>
                                      <button
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSkizzenPad({ anfrageId: anfrage.id, imageUrl: getFotoUrl(foto) });
                                        }}
                                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover/foto:opacity-100 transition flex items-center justify-center shadow"
                                        title="Skizzieren"
                                      >
                                        <PenTool className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Dokumente der Anfrage */}
                            {anfrage.dokumente && anfrage.dokumente.length > 0 && (
                              <div className="mb-4">
                                <p className="text-xs text-text-muted mb-2 flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  {anfrage.dokumente.length} {anfrage.dokumente.length === 1 ? 'Dokument' : 'Dokumente'}
                                </p>
                                <div className="space-y-2">
                                  {anfrage.dokumente.map((dok) => {
                                    const docStyle = getDocIcon(dok.dateityp);
                                    const DocIcon = docStyle.icon;
                                    const dokIsPdf = isPdf(dok.dateityp);
                                    return (
                                      <div
                                        key={dok.id}
                                        className="flex items-center gap-3 p-2 bg-white rounded-lg border border-border"
                                      >
                                        {dokIsPdf ? (
                                          <button
                                            onClick={() => handlePdfOpen(dok)}
                                            className={`p-2 rounded ${docStyle.bg} hover:opacity-80 transition cursor-pointer`}
                                            title="PDF im Browser öffnen"
                                          >
                                            <DocIcon className={`w-4 h-4 ${docStyle.color}`} />
                                          </button>
                                        ) : (
                                          <div className={`p-2 rounded ${docStyle.bg}`}>
                                            <DocIcon className={`w-4 h-4 ${docStyle.color}`} />
                                          </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium truncate">{dok.originalname}</p>
                                          <p className="text-xs text-text-muted">
                                            {(dok.groesse / 1024).toFixed(1)} KB
                                          </p>
                                        </div>
                                        <button
                                          onClick={() => handleDokumentDownload(dok)}
                                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                                          title="Herunterladen"
                                        >
                                          <Download className="w-4 h-4 text-text-muted" />
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Status Buttons */}
                            <div className="pt-3 border-t border-border">
                              <p className="text-xs text-text-muted mb-2">Status ändern:</p>
                              <div className="flex gap-2 flex-wrap">
                                <button
                                  onClick={() => handleStatusUpdate(anfrage.id, 'neu')}
                                  className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                                    anfrage.status === 'neu'
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                >
                                  Neu
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(anfrage.id, 'in_bearbeitung')}
                                  className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                                    anfrage.status === 'in_bearbeitung'
                                      ? 'bg-yellow-500 text-white'
                                      : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                  }`}
                                >
                                  In Bearbeitung
                                </button>
                                <button
                                  onClick={() => handleStatusUpdate(anfrage.id, 'abgeschlossen')}
                                  className={`px-3 py-1.5 text-xs rounded-full font-medium transition ${
                                    anfrage.status === 'abgeschlossen'
                                      ? 'bg-green-600 text-white'
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                  }`}
                                >
                                  Abgeschlossen
                                </button>
                              </div>
                            </div>

                            {/* Unterschriften-Sektion */}
                            <div className="mt-4 pt-4 border-t border-border">
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                  <FileSignature className="w-4 h-4 text-primary" />
                                  Unterschriften
                                </p>
                                <button
                                  onClick={() => setShowSignaturePad(anfrage.id)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                                >
                                  <FileSignature className="w-3 h-3" />
                                  Unterschrift hinzufügen
                                </button>
                              </div>
                              <UnterschriftenListe
                                key={refreshUnterschriften[anfrage.id] || 0}
                                anfrageId={anfrage.id}
                                kundeName={kunde.name}
                                onUpdate={() => setRefreshUnterschriften(prev => ({
                                  ...prev,
                                  [anfrage.id]: (prev[anfrage.id] || 0) + 1
                                }))}
                              />
                            </div>

                            {/* Arbeitsnachweis Button */}
                            <div className="mt-4 pt-4 border-t border-border">
                              <button
                                onClick={() => setShowArbeitsnachweisModal(anfrage.id)}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                              >
                                <FileText className="w-5 h-5" />
                                Arbeitsnachweis erstellen
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {/* Anfragen Pagination */}
                {anfragenTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                    <span className="text-xs text-text-muted">
                      {anfragenPage * ITEMS_PER_PAGE + 1}–{Math.min((anfragenPage + 1) * ITEMS_PER_PAGE, filteredAnfragen.length)} von {filteredAnfragen.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setAnfragenPage(p => Math.max(0, p - 1))}
                        disabled={anfragenPage === 0}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm px-2">{anfragenPage + 1} / {anfragenTotalPages}</span>
                      <button
                        onClick={() => setAnfragenPage(p => Math.min(anfragenTotalPages - 1, p + 1))}
                        disabled={anfragenPage >= anfragenTotalPages - 1}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              ) : (
                <div className="text-center py-8 text-text-muted">
                  <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>
                    Keine Anfragen gefunden
                    {anfrageSearchTerm && ` für "${anfrageSearchTerm}"`}
                    {anfrageStatusFilter && ` (Filter: ${anfrageStatusFilter === 'offen' ? 'offen' : 'erledigt'})`}
                  </p>
                  <button
                    onClick={() => {
                      setAnfrageSearchTerm('');
                      setAnfrageStatusFilter(null);
                    }}
                    className="mt-2 text-primary hover:underline text-sm"
                  >
                    Filter zurücksetzen
                  </button>
                </div>
              )
            ) : (
              <p className="text-text-muted text-sm">Keine Anfragen vorhanden</p>
            )}
            </div>

            {/* Projekte Tab */}
            <div className={activeAnfragenProjekteTab === 'projekte' ? 'block' : 'hidden'}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() => setProjektStatusFilter(projektStatusFilter === 'offen' ? null : 'offen')}
                      className={`px-2 py-1 rounded-full cursor-pointer transition-all ${
                        projektStatusFilter === 'offen'
                          ? 'bg-blue-500 text-white ring-2 ring-blue-300'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {offeneProjekte} offen
                    </button>
                    <button
                      onClick={() => setProjektStatusFilter(projektStatusFilter === 'erledigt' ? null : 'erledigt')}
                      className={`px-2 py-1 rounded-full cursor-pointer transition-all ${
                        projektStatusFilter === 'erledigt'
                          ? 'bg-green-500 text-white ring-2 ring-green-300'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {abgeschlosseneProjekte} erledigt
                    </button>
                  </div>
                </div>
                <Link
                  to={`/projekte/neu?kundeId=${id}`}
                  className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
                >
                  <Plus className="w-4 h-4" />
                  Neues Projekt
                </Link>
              </div>

              {/* Suchleiste für Projekte */}
              {projekte && projekte.length > 0 && (
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                    <input
                      type="text"
                      value={projektSearchTerm}
                      onChange={(e) => setProjektSearchTerm(e.target.value)}
                      placeholder="Projekte durchsuchen..."
                      className="w-full pl-10 pr-10 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {projektSearchTerm && (
                      <button
                        onClick={() => setProjektSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition"
                      >
                        <X className="w-4 h-4 text-text-muted" />
                      </button>
                    )}
                  </div>
                  {(projektSearchTerm || projektStatusFilter) && (
                    <p className="text-xs text-text-muted mt-2">
                      {filteredProjekte.length} von {projekte.length} Projekten
                      {projektStatusFilter && ` (${projektStatusFilter === 'offen' ? 'offen' : 'erledigt'})`}
                    </p>
                  )}
                </div>
              )}

              {projekte && projekte.length > 0 ? (
                filteredProjekte.length > 0 ? (
                <div className="space-y-3">
                  {filteredProjekte.map((projekt) => {
                    const isExpanded = expandedProjekte[projekt.id];
                    return (
                      <div key={projekt.id} className="border border-border rounded-lg overflow-hidden">
                        {/* Klickbarer Header */}
                        <button
                          onClick={() => toggleProjekt(projekt.id)}
                          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition text-left"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${getProjektStatusStyle(projekt.status)}`}>
                              {getProjektStatusLabel(projekt.status)}
                            </span>
                            <div className="min-w-0">
                              <h3 className="font-medium truncate">{projekt.titel}</h3>
                              <p className="text-xs text-text-muted">{projekt.nummer}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="text-xs text-text-muted hidden sm:block">
                              {projekt.startDatum ? formatDate(projekt.startDatum) : 'Kein Datum'}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-text-muted" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-text-muted" />
                            )}
                          </div>
                        </button>

                        {/* Ausgeklappter Inhalt */}
                        {isExpanded && (
                          <div className="px-4 pb-4 border-t border-border bg-gray-50/50">
                            <div className="pt-4">
                              {/* Aktions-Buttons */}
                              <div className="flex flex-wrap justify-end gap-2 sm:gap-4 mb-3">
                                <Link
                                  to={`/projekte/${projekt.id}`}
                                  className="flex items-center gap-1.5 px-3 py-1.5 sm:px-0 sm:py-0 text-sm text-primary hover:text-primary-dark sm:bg-transparent bg-primary-light rounded-lg sm:rounded-none transition"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  <span>Öffnen</span>
                                </Link>
                              </div>

                              {/* Beschreibung */}
                              {projekt.beschreibung && (
                                <div className="mb-4">
                                  <p className="text-xs text-text-muted mb-1">Beschreibung:</p>
                                  <p className="text-sm whitespace-pre-wrap">{projekt.beschreibung}</p>
                                </div>
                              )}

                              {/* Projekt-Details */}
                              <div className="flex flex-wrap gap-2 mb-4">
                                {projekt.startDatum && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                    Start: {formatDate(projekt.startDatum)}
                                  </span>
                                )}
                                {projekt.endDatum && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                    Ende: {formatDate(projekt.endDatum)}
                                  </span>
                                )}
                                {projekt.budgetNetto && (
                                  <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                                    Budget: {(projekt.budgetNetto / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                  </span>
                                )}
                              </div>

                              {/* Projektadresse (falls abweichend) */}
                              {(projekt.strasse || projekt.ort) && (
                                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                                  <p className="text-xs text-blue-700 font-medium mb-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    Projektadresse
                                  </p>
                                  <p className="text-sm">
                                    {projekt.strasse && <span>{projekt.strasse}, </span>}
                                    {projekt.plz && <span>{projekt.plz} </span>}
                                    {projekt.ort && <span>{projekt.ort}</span>}
                                  </p>
                                </div>
                              )}

                              {/* Verknüpfte Anfragen */}
                              {projekt.anfragen && projekt.anfragen.length > 0 && (
                                <div className="mb-4 p-3 bg-primary-light rounded-lg">
                                  <p className="text-xs text-primary font-medium mb-2 flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" />
                                    Verknüpfte Anfragen ({projekt._count?.anfragen || projekt.anfragen.length})
                                  </p>
                                  <div className="space-y-1">
                                    {projekt.anfragen.slice(0, 3).map(a => (
                                      <p key={a.id} className="text-sm truncate">{a.betreff}</p>
                                    ))}
                                    {(projekt._count?.anfragen || projekt.anfragen.length) > 3 && (
                                      <p className="text-xs text-text-muted">+ {(projekt._count?.anfragen || projekt.anfragen.length) - 3} weitere</p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Verknüpfte Rechnungen */}
                              {projekt.rechnungen && projekt.rechnungen.length > 0 && (
                                <div className="mb-4">
                                  <p className="text-xs text-text-muted mb-2 flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {projekt._count?.rechnungen || projekt.rechnungen.filter(r => !r.storniert).length} {(projekt._count?.rechnungen || projekt.rechnungen.filter(r => !r.storniert).length) === 1 ? 'Rechnung' : 'Rechnungen'}
                                  </p>
                                  <div className="space-y-2">
                                    {projekt.rechnungen.filter(r => !r.storniert).slice(0, 3).map(r => (
                                      <div
                                        key={r.id}
                                        className="flex items-center gap-3 p-2 bg-white rounded-lg border border-border"
                                      >
                                        {r.pdfPfad ? (
                                          <a
                                            href={rechnungApi.getPdfUrl(r)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="p-2 rounded bg-red-50 hover:bg-red-100 transition cursor-pointer"
                                            title="PDF öffnen"
                                          >
                                            <File className="w-4 h-4 text-red-600" />
                                          </a>
                                        ) : (
                                          <div className="p-2 rounded bg-gray-100">
                                            <FileText className="w-4 h-4 text-gray-500" />
                                          </div>
                                        )}
                                        <Link
                                          to={`/rechnungen/${r.id}`}
                                          className="flex-1 min-w-0 hover:text-primary transition"
                                        >
                                          <p className="text-sm font-medium truncate">{r.rechnungsnummer}</p>
                                          <p className="text-xs text-text-muted">
                                            {(r.bruttogesamt / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                                            {' · '}
                                            {new Date(r.datum).toLocaleDateString('de-DE')}
                                          </p>
                                        </Link>
                                        <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
                                          r.status === 'bezahlt' ? 'bg-green-100 text-green-700' :
                                          r.status === 'versendet' ? 'bg-blue-100 text-blue-700' :
                                          'bg-gray-100 text-gray-700'
                                        }`}>
                                          {r.status === 'bezahlt' ? 'Bezahlt' :
                                           r.status === 'versendet' ? 'Offen' :
                                           r.status === 'entwurf' ? 'Entwurf' : r.status}
                                        </span>
                                      </div>
                                    ))}
                                    {(projekt._count?.rechnungen || projekt.rechnungen.filter(r => !r.storniert).length) > 3 && (
                                      <Link
                                        to={`/projekte/${projekt.id}`}
                                        className="text-xs text-primary hover:underline"
                                      >
                                        + {(projekt._count?.rechnungen || projekt.rechnungen.filter(r => !r.storniert).length) - 3} weitere anzeigen
                                      </Link>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Unterschriften-Sektion */}
                              <div className="mt-4 pt-4 border-t border-border">
                                <div className="flex items-center justify-between mb-3">
                                  <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                    <FileSignature className="w-4 h-4 text-primary" />
                                    Unterschriften
                                  </p>
                                  <button
                                    onClick={() => setShowProjektSignaturePad(projekt.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                                  >
                                    <FileSignature className="w-3 h-3" />
                                    Unterschrift hinzufügen
                                  </button>
                                </div>
                                <UnterschriftenListe
                                  key={refreshProjektUnterschriften[projekt.id] || 0}
                                  projektId={projekt.id}
                                  kundeName={kunde.name}
                                  onUpdate={() => setRefreshProjektUnterschriften(prev => ({
                                    ...prev,
                                    [projekt.id]: (prev[projekt.id] || 0) + 1
                                  }))}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                ) : (
                  <div className="text-center py-8 text-text-muted">
                    <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>
                      Keine Projekte gefunden
                      {projektSearchTerm && ` für "${projektSearchTerm}"`}
                      {projektStatusFilter && ` (Filter: ${projektStatusFilter === 'offen' ? 'offen' : 'erledigt'})`}
                    </p>
                    <button
                      onClick={() => {
                        setProjektSearchTerm('');
                        setProjektStatusFilter(null);
                      }}
                      className="mt-2 text-primary hover:underline text-sm"
                    >
                      Filter zurücksetzen
                    </button>
                  </div>
                )
              ) : (
                <p className="text-text-muted text-sm">Keine Projekte vorhanden</p>
              )}
            </div>
          </motion.div>

          {/* Termine Box - slides from right */}
          <motion.div
            className="bg-surface rounded-card shadow-card p-6"
            initial={{ opacity: 0, x: 15 }}
            animate={{
              opacity: isContentVisible ? 1 : 0,
              x: isContentVisible ? 0 : 15
            }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-success" />
                <h2 className="text-xl font-semibold">
                  Termine ({kunde.termine?.length || 0})
                </h2>
              </div>
              <Link
                to={`/kalender?neuerTermin=true&kundeId=${id}&kundeName=${encodeURIComponent(kunde.name)}&kundeEmail=${encodeURIComponent(kunde.email || '')}&kundeAdresse=${encodeURIComponent(kunde.adresse || '')}`}
                className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-primary-dark transition"
              >
                <Plus className="w-4 h-4" />
                Neuer Termin
              </Link>
            </div>

            {kunde.termine && kunde.termine.length > 0 ? (
              <div className="space-y-4">
                {paginatedTermine.map((termin) => (
                  <Link
                    key={termin.id}
                    to={`/kalender?terminId=${termin.id}`}
                    className="block w-full text-left border border-border rounded-lg p-4 hover:bg-gray-50 hover:border-primary transition"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium">
                          {formatDate(termin.datum)} um {termin.uhrzeit} Uhr
                        </p>
                        {termin.titel && (
                          <p className="text-sm font-medium text-text mt-0.5">{termin.titel}</p>
                        )}
                        {termin.beschreibung && (
                          <p className="text-sm text-text-muted mt-1">{termin.beschreibung}</p>
                        )}
                      </div>
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: termin.farbe || '#057867' }}
                        title="Terminfarbe"
                      />
                    </div>
                    {termin.ort && (
                      <div className="flex items-center gap-1 text-xs text-text-muted mt-2">
                        <MapPin className="w-3 h-3" />
                        <span>{termin.ort}</span>
                      </div>
                    )}
                  </Link>
                ))}
                {/* Termine Pagination */}
                {termineTotalPages > 1 && (
                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <span className="text-xs text-text-muted">
                      {terminePage * ITEMS_PER_PAGE + 1}–{Math.min((terminePage + 1) * ITEMS_PER_PAGE, termineList.length)} von {termineList.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.preventDefault(); setTerminePage(p => Math.max(0, p - 1)); }}
                        disabled={terminePage === 0}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm px-2">{terminePage + 1} / {termineTotalPages}</span>
                      <button
                        onClick={(e) => { e.preventDefault(); setTerminePage(p => Math.min(termineTotalPages - 1, p + 1)); }}
                        disabled={terminePage >= termineTotalPages - 1}
                        className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-text-muted text-sm">Keine Termine vorhanden</p>
            )}
          </motion.div>
        </div>

        {/* Aktivitäten-Timeline */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0, y: 15 }}
          animate={{
            opacity: isContentVisible ? 1 : 0,
            y: isContentVisible ? 0 : 15
          }}
          transition={{ duration: 0.2, ease: 'easeOut', delay: 0.05 }}
        >
          <AktivitaetenTimeline kundeId={id} refreshTrigger={refreshTimeline} />
        </motion.div>

        {/* Objekte Section (nur bei Hausverwaltungen) */}
        {kunde?.kundenTyp === 'HAUSVERWALTUNG' && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-4 sm:p-6 mb-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: isContentVisible ? 1 : 0,
              y: isContentVisible ? 0 : 15
            }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.055 }}
          >
            <ObjekteSection
              kundeId={id}
              initialObjekte={kunde?.objekte}
              anfragen={kunde?.anfragen}
              readOnly={!canEditKunden}
              onScrollToAnfrage={(anfrageId) => {
                setExpandedAnfragen(prev => ({ ...prev, [anfrageId]: true }));
                setTimeout(() => {
                  const el = anfrageRefs.current[anfrageId];
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }}
            />
          </motion.div>
        )}

        {/* Anlagen & Wartung Section */}
        {!isHeykikiOrg && (
          <motion.div
            className="bg-surface rounded-card shadow-card p-4 sm:p-6 mb-6"
            initial={{ opacity: 0, y: 15 }}
            animate={{
              opacity: isContentVisible ? 1 : 0,
              y: isContentVisible ? 0 : 15
            }}
            transition={{ duration: 0.2, ease: 'easeOut', delay: 0.06 }}
          >
            <AnlagenSection
              kundeId={id}
              initialAnlagen={kunde?.anlagen}
              readOnly={!canEditKunden}
            />
          </motion.div>
        )}

        {/* Fotos & Dokumente Section with Tabs - fades in from bottom */}
        <motion.div
          className="bg-surface rounded-card shadow-card p-4 sm:p-6 mb-6 overflow-x-hidden"
          initial={{ opacity: 0, y: 15 }}
          animate={{
            opacity: isContentVisible ? 1 : 0,
            y: isContentVisible ? 0 : 15
          }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          {/* Tab Headers mit Bulk-Download Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border mb-4 gap-2">
            <div className="flex">
              <button
                onClick={() => setActiveMediaTab('fotos')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm border-b-2 transition ${
                  activeMediaTab === 'fotos'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >
                <ImageIcon className="w-4 sm:w-5 h-4 sm:h-5" />
                Fotos ({fotos.length})
              </button>
              <button
                onClick={() => setActiveMediaTab('dokumente')}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-3 font-medium text-xs sm:text-sm border-b-2 transition ${
                  activeMediaTab === 'dokumente'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >
                <FileText className="w-4 sm:w-5 h-4 sm:h-5" />
                Dokumente ({dokumente.length})
              </button>
            </div>
            {/* Bulk-Download Button - nur für aktiven Tab */}
            {activeTabFileCount > 0 && (
              <button
                onClick={handleBulkDownload}
                disabled={isDownloading}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm shrink-0"
              >
                <Download className="w-4 h-4" />
                {isDownloading ? (
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <span className="hidden sm:inline">
                      Alle {activeMediaTab === 'fotos' ? 'Fotos' : 'Dokumente'} herunterladen ({activeTabFileCount})
                    </span>
                    <span className="sm:hidden">
                      Alle ({activeTabFileCount})
                    </span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Tab Content - beide Tabs bleiben gemountet, nur CSS visibility */}
          <div className={activeMediaTab === 'fotos' ? 'block' : 'hidden'}>
            <FotoUpload kundeId={id} fotos={fotos} anfragen={kunde.anfragen} onFotosChange={() => { loadFotos(); loadKunde(); setRefreshTimeline(prev => prev + 1); }} readOnly={!canEditKunden} onSkizzieren={(foto) => setShowSkizzenPad({ anfrageId: foto.anfrageId || null, imageUrl: getFotoUrl(foto) })} />
          </div>
          <div className={activeMediaTab === 'dokumente' ? 'block' : 'hidden'}>
            <DokumenteTab kundeId={id} anfragen={kunde.anfragen || []} projekte={projekte || []} ansprechpartner={kunde.status === 'lieferant' && !kunde.hauptLieferantId ? (kunde.ansprechpartner || []) : []} onDokumenteChange={() => { loadDokumente(); loadKunde(); setRefreshTimeline(prev => prev + 1); }} readOnly={!canEditKunden} />
          </div>
        </motion.div>

        {/* Footer with timestamps */}
        <motion.div
          className="bg-surface rounded-card shadow-card p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: isContentVisible ? 1 : 0 }}
          transition={{ duration: 0.3, delay: 0.25 }}
        >
          <div className="flex gap-6 text-xs text-text-muted">
            <span>Erstellt: {formatDateTime(kunde.erstelltAm)}</span>
            <span>Zuletzt aktualisiert: {formatDateTime(kunde.aktualisiertAm)}</span>
          </div>
        </motion.div>

        {/* Modal for editing kunde */}
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setError('');
          }}
          title="Kunde bearbeiten"
        >
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <KundenForm
            kunde={kunde}
            onSubmit={handleUpdateKunde}
            onCancel={() => {
              setIsModalOpen(false);
              setError('');
            }}
            isLoading={isSaving}
            hasAnsprechpartner={kunde?.ansprechpartner?.length > 0}
          />

          {/* Delete button inside edit modal */}
          <div className="mt-6 pt-6 border-t border-border">
            <button
              onClick={() => {
                setIsModalOpen(false);
                setIsDeleteModalOpen(true);
              }}
              className="w-full flex items-center justify-center gap-2 bg-danger text-white px-4 py-3 rounded-button font-medium hover:bg-red-700 transition"
            >
              <Trash2 className="w-4 h-4" />
              Kunde löschen
            </button>
          </div>
        </Modal>

        {/* Modal für neuen Ansprechpartner */}
        <Modal
          isOpen={isApModalOpen}
          onClose={() => {
            setIsApModalOpen(false);
            setError('');
          }}
          title="Neuer Ansprechpartner"
        >
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
          <KundenForm
            hideStatusPicker
            onSubmit={async (formData) => {
              setIsSaving(true);
              setError('');
              try {
                await createKunde({ ...formData, hauptLieferantId: kunde.id });
                setIsApModalOpen(false);
                loadKunde();
              } catch (err) {
                setError(err.response?.data?.error || 'Fehler beim Erstellen');
              } finally {
                setIsSaving(false);
              }
            }}
            onCancel={() => {
              setIsApModalOpen(false);
              setError('');
            }}
            isLoading={isSaving}
          />
        </Modal>

        {/* Modal for deleting kunde */}
        <Modal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          title="Kunde löschen"
        >
          <div className="space-y-4">
            <p className="text-text">
              Möchten Sie den Kunden <strong>{kunde?.name}</strong> wirklich löschen?
            </p>
            <p className="text-sm text-text-muted">
              Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen Anfragen, Termine und Fotos werden ebenfalls gelöscht.
            </p>
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleDeleteKunde}
                className="flex-1 bg-danger text-white py-3 rounded-button font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeleting}
              >
                {isDeleting ? 'Wird gelöscht...' : 'Ja, löschen'}
              </button>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
                disabled={isDeleting}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </Modal>

        {/* Modal for creating/editing anfrage */}
        <Modal
          isOpen={isAnfrageModalOpen}
          onClose={() => {
            setIsAnfrageModalOpen(false);
            setEditingAnfrage(null);
            setAnfrageError('');
          }}
          title={editingAnfrage ? 'Anfrage bearbeiten' : 'Neue Anfrage'}
        >
          {anfrageError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {anfrageError}
            </div>
          )}
          <AnfrageForm
            anfrage={editingAnfrage}
            onSubmit={editingAnfrage ? handleUpdateAnfrage : handleCreateAnfrage}
            onCancel={() => {
              setIsAnfrageModalOpen(false);
              setEditingAnfrage(null);
              setAnfrageError('');
            }}
            isLoading={isAnfrageSaving}
            objekte={kunde?.kundenTyp === 'HAUSVERWALTUNG' ? kunde?.objekte : null}
          />

          {/* Delete button - nur beim Bearbeiten sichtbar */}
          {editingAnfrage && (
            <div className="mt-6 pt-6 border-t border-border">
              <button
                onClick={() => {
                  setIsAnfrageModalOpen(false);
                  setIsDeleteAnfrageModalOpen(true);
                }}
                className="w-full flex items-center justify-center gap-2 bg-danger text-white px-4 py-3 rounded-button font-medium hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                Anfrage löschen
              </button>
            </div>
          )}
        </Modal>

        {/* Modal for deleting anfrage */}
        <Modal
          isOpen={isDeleteAnfrageModalOpen}
          onClose={() => setIsDeleteAnfrageModalOpen(false)}
          title="Anfrage löschen"
        >
          <div className="space-y-4">
            <p className="text-text">
              Möchten Sie die Anfrage <strong>"{editingAnfrage?.betreff}"</strong> wirklich löschen?
            </p>
            <p className="text-sm text-text-muted">
              Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen Fotos und Dokumente der Anfrage werden ebenfalls gelöscht.
            </p>
            {anfrageError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {anfrageError}
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button
                onClick={handleDeleteAnfrage}
                className="flex-1 bg-danger text-white py-3 rounded-button font-medium hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDeletingAnfrage}
              >
                {isDeletingAnfrage ? 'Wird gelöscht...' : 'Ja, löschen'}
              </button>
              <button
                onClick={() => setIsDeleteAnfrageModalOpen(false)}
                className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
                disabled={isDeletingAnfrage}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </Modal>

        {/* Modal für Aufmaß */}
        <AufmassModal
          isOpen={isAufmassModalOpen}
          onClose={() => {
            setIsAufmassModalOpen(false);
            setSelectedAnfrageForAufmass(null);
            setEditingAufmass(null);
          }}
          onSave={handleSaveAufmass}
          isLoading={isAufmassSaving}
          editingAufmass={editingAufmass}
        />

        {/* Modal für SignaturePad (Anfragen) */}
        {showSignaturePad && (
          <SignaturePad
            onSave={(data) => handleSaveUnterschrift(showSignaturePad, data)}
            onClose={() => setShowSignaturePad(null)}
          />
        )}

        {/* Modal für SignaturePad (Projekte) */}
        {showProjektSignaturePad && (
          <SignaturePad
            onSave={(data) => handleSaveProjektUnterschrift(showProjektSignaturePad, data)}
            onClose={() => setShowProjektSignaturePad(null)}
          />
        )}

        {/* Modal für SkizzenPad */}
        {showSkizzenPad && (
          <SkizzenPad
            onSave={(file) => handleSaveSkizze(showSkizzenPad.anfrageId, file)}
            onClose={() => setShowSkizzenPad(null)}
            saving={isSkizzeSaving}
            initialImageUrl={showSkizzenPad.imageUrl || null}
          />
        )}

        {/* Modal für Arbeitsnachweis */}
        {showArbeitsnachweisModal && (
          <ArbeitsnachweisModal
            anfrageId={showArbeitsnachweisModal}
            kunde={kunde}
            onClose={() => setShowArbeitsnachweisModal(null)}
            onGenerated={() => {
              setRefreshTimeline(prev => prev + 1);
              loadDokumente();
              loadKunde();
            }}
          />
        )}

        {/* Google Maps Preview Popup */}
        {showMapPreview && kunde.adresse && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            style={{
              top: mapPosition.top,
              left: mapPosition.left,
              width: '320px',
              maxWidth: 'calc(100vw - 16px)'
            }}
            onMouseEnter={handleMapMouseEnter}
            onMouseLeave={handleMapMouseLeave}
          >
            <div className="relative">
              <iframe
                title="Google Maps Preview"
                width="100%"
                height="200"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(kunde.adresse)}&output=embed&z=15`}
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/10 to-transparent" />
            </div>
            <div className="p-3 bg-white">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{kunde.adresse}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Klicken für Vollbild in Google Maps</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* iOS Share Dialog */}
        {showIOSShareDialog && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={closeIOSShareDialog}
            />
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 100 }}
              className="fixed left-4 right-4 bottom-8 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-primary-light rounded-xl">
                    <Download className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">Datei speichern</h3>
                    <p className="text-sm text-gray-500 truncate">{iosShareData?.filename}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Share-Button (öffnet natives iOS Share-Menü) */}
                  <button
                    onClick={handleIOSShare}
                    className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3.5 rounded-xl font-medium hover:bg-primary-dark transition"
                  >
                    <Share2 className="w-5 h-5" />
                    Teilen / In Dateien sichern
                  </button>

                  {/* Öffnen-Button */}
                  <button
                    onClick={() => {
                      if (iosShareData?.url) {
                        window.open(iosShareData.url, '_blank');
                      }
                      closeIOSShareDialog();
                    }}
                    className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3.5 rounded-xl font-medium hover:bg-gray-200 transition"
                  >
                    <Eye className="w-5 h-5" />
                    Vorschau öffnen
                  </button>

                  {/* Abbrechen */}
                  <button
                    onClick={closeIOSShareDialog}
                    className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </motion.div>
    </Layout>
  );
}

export default Kundendetail;
